// A2F-545: IND Starter Pack rule definitions.
//
// Four search-type rules from docs/specs/chrona-rule-executor-spec.md
// §"Cover letter validation example (multi-check)". Patterns, messages,
// severities, and `invertMatch: true` (flag when pattern is NOT found —
// the required-element pattern) come verbatim from the spec; do not
// paraphrase or rewrite the messages without a rule-executor-spec
// amendment.
//
// Kept in their own file so the pack content has a clean git blame. The
// tag-rules.ts `R` registry imports them individually and the `ALL_RULES`
// aggregate spreads them in, so the prebuild validator
// (scripts/validate-tag-rules.mjs) and `ALL_RULES_BY_ID` lookup resolve
// them like any other rule.
//
// Rules are pack-only — no tag mapping. The PackRail's IND card is the
// only acquisition affordance; users who don't toggle the pack never
// see these rules.

import type { SearchRule } from '../types'

// A2F-1013: `contactRole` routes the executor to the deterministic cover-letter
// contact extractor (signature-block + contact-intent + mailto/bare email parsing)
// instead of the coarse regex. The `searches[]` entry is retained verbatim as
// the documented fallback (absent-contactRole path) and its message/severity are
// reused for the not-found finding. The backend's _validate_search only checks
// searches[].pattern/severity/message and ignores unknown rule-level fields, so
// no backend change is required to save/load these rules.

export const ind_cover_letter_regulatory_contact: SearchRule = {
  id: 'ind_cover_letter_regulatory_contact',
  type: 'search',
  tier: 'pro',
  privacy: 'local',
  label: 'Regulatory contact required',
  icon: 'shield',
  description: 'Cover letter must name a regulatory contact with email.',
  contactRole: 'regulatory',
  searches: [
    {
      pattern: 'regulatory\\s*(contact|point of contact)',
      useRegex: true,
      caseSensitive: false,
      severity: 'fail',
      invertMatch: true,
      message:
        'No regulatory contact found. FDA requires regulatory point of contact with email.',
    },
  ],
}

export const ind_cover_letter_technical_contact: SearchRule = {
  id: 'ind_cover_letter_technical_contact',
  type: 'search',
  tier: 'pro',
  privacy: 'local',
  label: 'Technical contact required',
  icon: 'shield',
  description: 'Cover letter must name a technical contact with email.',
  contactRole: 'technical',
  searches: [
    {
      pattern: 'technical\\s*(contact|point of contact)',
      useRegex: true,
      caseSensitive: false,
      severity: 'fail',
      invertMatch: true,
      message:
        'No technical contact found. FDA requires technical point of contact with email.',
    },
  ],
}

export const ind_cover_letter_submission_size: SearchRule = {
  id: 'ind_cover_letter_submission_size',
  type: 'search',
  tier: 'pro',
  privacy: 'local',
  label: 'Submission size noted',
  icon: 'shield',
  description:
    'Cover letter should include the approximate submission size.',
  searches: [
    {
      pattern: '\\d+(\\.\\d+)?\\s*(KB|MB|GB|TB|megabytes|gigabytes)',
      useRegex: true,
      caseSensitive: false,
      severity: 'warn',
      invertMatch: true,
      message:
        'No submission size found in technical description. FDA recommends including approximate submission size.',
    },
  ],
}

export const ind_cover_letter_virus_free: SearchRule = {
  id: 'ind_cover_letter_virus_free',
  type: 'search',
  tier: 'pro',
  privacy: 'local',
  label: 'Virus-free statement required',
  icon: 'shield',
  description:
    'Cover letter must attest the submission is virus-free and name the AV tool.',
  searches: [
    {
      pattern: 'virus[\\-\\s]?free|free\\s+of\\s+virus',
      useRegex: true,
      caseSensitive: false,
      severity: 'fail',
      invertMatch: true,
      message:
        'No virus-free statement found. Include statement naming AV software, version, and vendor.',
    },
  ],
}

// A2F-797: Rolling submission phrasing. Direction differs from the
// other four cover-letter rules — those use invertMatch:true to flag
// REQUIRED elements that must be present. This rule uses
// invertMatch:false (default) because the required structure for a
// rolling cover letter (specific portion submitted now, anticipated
// dates for remaining portions per FDA Technical Conformance Guide)
// can't be matched with a single search pattern, and a presence-flag
// would false-positive on every non-rolling IND cover letter. Instead
// we flag the keyword and surface a warning that prompts the writer
// to verify the structure when they're actually composing a rolling
// cover letter. Severity warn per SME (A2F-797).
//
// Pattern tolerates "rolling submission", "rolling-submission",
// "rolling review", "rolling installment", "rolling amendment". The
// [\-\s]+ separator mirrors the precedent in ind_cover_letter_virus_free
// for hyphenated regulatory phrasing.
export const ind_cover_letter_rolling_phrasing: SearchRule = {
  id: 'ind_cover_letter_rolling_phrasing',
  type: 'search',
  tier: 'pro',
  privacy: 'local',
  label: 'Rolling submission phrasing',
  icon: 'shield',
  description:
    'Flag mentions of "rolling submission" so the writer can verify the required structure: portion being submitted now and anticipated dates for the remaining portions.',
  searches: [
    {
      pattern: '\\brolling[\\-\\s]+(submission|review|installment|amendment)\\b',
      useRegex: true,
      caseSensitive: false,
      severity: 'warn',
      message:
        'Cover letter mentions a rolling submission — confirm it identifies the specific portion being submitted now and anticipated dates for the remaining portions per FDA Technical Conformance Guide.',
    },
  ],
}

export const IND_PACK_RULES = [
  ind_cover_letter_regulatory_contact,
  ind_cover_letter_technical_contact,
  ind_cover_letter_submission_size,
  ind_cover_letter_virus_free,
  ind_cover_letter_rolling_phrasing,
] as const
